import * as Phaser from "phaser-ce"
import { log } from "../../../../common/Logging"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { GenericAssets, loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ProjectorSprite } from "../../../generic-sprites/ProjectorSprite"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { TitleSprite } from "../../../generic-sprites/TitleSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom, ZoomInfo, ZoomTarget } from "../../../utils/CameraZoom"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { AddictionRepo } from "../AddictionRepo"
import { ActionDog } from "./ActionDog"
import { MenPos } from "./MenPos"
import { loadAssets, MentalAssets } from "./MentalAssets"

export class MentalState extends Phaser.State {

    private static readonly RING = "ring"
    private static readonly THOUGHT = "thought"
    private static readonly COFFEE_SCENE_SIZE_LARGE = 0.9
    private static readonly COFFEE_SCENE_SIZE_SMALL = 0.66

    private scientist: ScientistSprite
    private dog: ActionDog
    private pavlov: Phaser.Sprite
    private meatBowl: Phaser.Sprite
    private projector: ProjectorSprite
    private title: TitleSprite
    private rug: Phaser.Sprite
    private coffeeAlone: Phaser.Sprite
    private coffeeTogether: Phaser.Sprite
    private coffeeOnly: Phaser.Sprite
    private coffeeSmell: Phaser.Sprite
    private coffeeCup: Phaser.Sprite
    private ecig: Phaser.Sprite
    private bellSound: Phaser.Sound

    private readonly words: Array<Word> = [
        new Word(400, 900, [Sound.MBP, Sound.L], "Mental"),
        new Word(1050, 1350, [Sound.A, Sound.CON], "addiction"),
        new Word(1350, 1750, [Sound.A, Sound.MBP], "happens"),
        new Word(1800, 1950, [Sound.CON], "when"),
        new Word(2000, 2200, [Sound.MBP, Sound.L], "people"),
        new Word(2250, 2500, [Sound.CON], "start"),
        new Word(2550, 2800, [Sound.CON, Sound.A], "to make"),
        new Word(2850, 3500, [Sound.CON, Sound.O, Sound.CON], "connections"),
        new Word(3550, 3900, [Sound.CON, Sound.E], "between"),
        new Word(3900, 4250, [Sound.CON, Sound.A], "certain"),
        new Word(4250, 5050, [Sound.A, Sound.CON, Sound.FV, Sound.CON], "activities"),
        new Word(5250, 5750, [Sound.A, Sound.U], "and using"),
        new Word(5850, 6350, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco"),

        new Word(7250, 7600, [Sound.O, Sound.MBP, Sound.A, Sound.FV], "You may have"),
        new Word(7650, 7830, [Sound.E, Sound.CON], "heard"),
        new Word(7830, 8045, [Sound.A, Sound.CON], "about"),
        new Word(8185, 8780, [Sound.MBP, Sound.A, Sound.FV], "Pavlov's"),
        new Word(8850, 9027, [Sound.CON, Sound.O], "dog"),

        new Word(37414, 37705, [Sound.E, Sound.FV], "Even"),
        new Word(37844, 38066, [Sound.E, Sound.CON], "When there"),
        new Word(38067, 38383, [Sound.A, Sound.CON], "wasn't"),
        new Word(38384, 38693, [Sound.A, Sound.MBP, Sound.E], "any meat"),
        new Word(38769, 39314, [Sound.A, Sound.CON], "around"),

        new Word(40087, 40454, [Sound.CON, Sound.A, Sound.MBP], "The same"),
        new Word(40524, 40752, [Sound.CON, Sound.A], "kind of (kinda)"),
        new Word(40879, 41373, [Sound.MBP, Sound.O, Sound.CON], "process"),
        new Word(41374, 41886, [Sound.A, Sound.MBP, Sound.E, Sound.CON], "happens when"),
        new Word(41949, 42488, [Sound.MBP, Sound.E, Sound.O, Sound.CON], "people smoke"),
        new Word(42640, 43014, [Sound.O, Sound.FV, Sound.MBP], "or vape."),

        new Word(43800, 44000, [Sound.CON, Sound.A], "There are"),
        new Word(44001, 44338, [Sound.MBP, Sound.A], "many"),
        new Word(44339, 44756, [Sound.CON, Sound.A, Sound.CON], "things that"),
        new Word(44864, 45054, [Sound.MBP], "people"),
        new Word(45111, 45320, [Sound.CON, Sound.O], "connect"),
        new Word(45427, 45978, [Sound.CON, Sound.FV, Sound.A], "with vaping"),
        new Word(46536, 46618, [Sound.CON], "like"),
        new Word(46682, 47000, [Sound.CON, Sound.A], "certain"),
        new Word(47100, 47600, [Sound.MBP, Sound.A], "places"),
        new Word(47708, 47879, [Sound.O, Sound.A], "or ac"),
        new Word(48012, 48563, [Sound.CON, Sound.FV, Sound.CON], "tivities"),

        new Word(49146, 49800, [Sound.FV, Sound.O, Sound.E, Sound.MBP], "For example"),
        new Word(50300, 50983, [Sound.CON, Sound.FV], "some vapers"),
        new Word(51110, 51243, [Sound.CON, Sound.O], "connect"),
        new Word(51433, 51743, [Sound.FV, Sound.A], "vaping"),

        new Word(64305, 64609, [Sound.CON], "This is"),
        new Word(64610, 65015, [Sound.A, Sound.CON, Sound.L], "what it's like"),
        new Word(65129, 65262, [Sound.O, Sound.E], "to be"),
        new Word(65263, 65838, [Sound.MBP, Sound.CON, Sound.A], "mentally"),
        new Word(65839, 65933, [Sound.A], "a"),
        new Word(66035, 66085, [Sound.CON], "ddict"),
        new Word(66187, 66269, [Sound.E, Sound.CON], "ed"),
        new Word(66345, 66415, [Sound.O], "to"),
        new Word(66503, 67000, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco"),

        new Word(67827, 68112, [Sound.E], "Every"),
        new Word(68182, 68360, [Sound.CON, Sound.A], "time a"),
        new Word(68461, 68695, [Sound.E, Sound.O], "person"),
        new Word(68803, 69506, [Sound.O, Sound.A], "is somewhere"),
        new Word(69620, 69734, [Sound.O], "or"),
        new Word(69817, 70222, [Sound.CON, Sound.O], "does some"),
        new Word(70330, 70944, [Sound.CON, Sound.A, Sound.L], "thing that's linked"),
        new Word(71039, 71723, [Sound.CON, Sound.O], "to smoking"),
        new Word(72034, 72141, [Sound.A], "vap"),
        new Word(72230, 72553, [Sound.O], "ing or"),
        new Word(72680, 72952, [Sound.CON], "dipping,"),
        new Word(73617, 74055, [Sound.A, Sound.MBP], "their mind"),
        new Word(74056, 74580, [Sound.A, Sound.CON, Sound.A], "makes them want"),
        new Word(74663, 75106, [Sound.CON, Sound.O], "to smoke"),
        new Word(75607, 75752, [Sound.FV, Sound.A], "vape"),
        new Word(75949, 76107, [Sound.O], "or"),
        new Word(76253, 76481, [Sound.CON], "dip."),
    ]

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        this.game.world.setBounds(0, 0, Scale.width() * 2, Scale.height())

        const background = this.game.add.sprite(0, 0, MentalAssets.BACKGROUND)
        background.width = Scale.width() * 2
        background.height = Scale.height()

        this.title = new TitleSprite(this.game, MenPos.titleY(), "Mental Addiction")

        this.rug = this.game.add.sprite(MenPos.rugX(), MenPos.rugY(), MentalAssets.CARPET)
        Scale.scaleSprite(this.rug, 0.6)
        this.dog = new ActionDog(this.game)

        this.pavlov = this.game.add.sprite(MenPos.pavlovX(), MenPos.pavlovY(), MentalAssets.PAVLOV)
        Scale.scaleSprite(this.pavlov, 0.45)
        this.pavlov.alpha = 0

        this.meatBowl = this.game.add.sprite(MenPos.foodDishX(), MenPos.foodDishY(), MentalAssets.MEAT_BOWL)
        Scale.scaleSprite(this.meatBowl, 0.45)

        this.scientist = new ScientistSprite(this.game, MenPos.scientistX(), MenPos.scientistY(),
            MentalAssets.AUDIO, 0.88, this.words, undefined, ActivityKey.ADDICTION_MAIN, () => {
                AddictionRepo.instance.setMentalAsComplete()
            })

        this.projector = new ProjectorSprite(this.game, MenPos.projectorX(), MenPos.projectorY(), 0.47)
        const projectorMask = this.projector.projectorMask

        this.coffeeAlone =
            this.game.add.sprite(MenPos.coffeeSceneX(), MenPos.coffeeSceneY(), MentalAssets.COFFEE_ALONE)
        Scale.scaleSprite(this.coffeeAlone, MentalState.COFFEE_SCENE_SIZE_LARGE)
        this.coffeeAlone.alpha = 0
        this.coffeeAlone.mask = projectorMask

        this.coffeeTogether =
            this.game.add.sprite(MenPos.coffeeSceneX(), MenPos.coffeeSceneY2(), MentalAssets.COFFEE_TOGETHER)
        Scale.scaleSprite(this.coffeeTogether, MentalState.COFFEE_SCENE_SIZE_SMALL)
        this.coffeeTogether.alpha = 0
        this.coffeeTogether.mask = projectorMask

        this.coffeeOnly =
            this.game.add.sprite(MenPos.coffeeSceneX(), MenPos.coffeeSceneY2(), MentalAssets.COFFEE_ONLY)
        Scale.scaleSprite(this.coffeeOnly, 0.55)
        this.coffeeOnly.alpha = 0
        this.coffeeOnly.mask = projectorMask

        this.coffeeSmell =
            this.game.add.sprite(MenPos.coffeeSmellX(), MenPos.coffeeSmellY(), MentalAssets.COFFEE_SMELL)
        Scale.scaleSprite(this.coffeeSmell, 0.8)
        this.coffeeSmell.alpha = 0
        const smell = "smell"
        this.coffeeSmell.animations.add(smell)
        this.coffeeSmell.animations.play(smell, 14, true)

        this.coffeeCup = this.game.add.sprite(MenPos.coffeeX(), MenPos.coffeeY(), MentalAssets.COFFEE)
        Scale.scaleSprite(this.coffeeCup, 0.65)
        this.coffeeCup.mask = projectorMask

        this.ecig = this.game.add.sprite(MenPos.ecigX(), MenPos.ecigY(), GenericAssets.ECIG)
        Scale.scaleSprite(this.ecig, 0.9)
        this.ecig.mask = projectorMask

        this.bellSound = this.game.add.audio(GenericAssets.BELL_SOUND)

        this.setupTimers()
    }

    setupTimers = () => {
        let undoZoom: ZoomInfo
        const oneOffs = new AudioSync(this.game, this.scientist.audio)

        oneOffs.add(8000, () => {
            const zoomTarget = new ZoomTarget()
            zoomTarget.x = MenPos.rugX()
            zoomTarget.y = this.dog.dog.y - Scale.height() * 0.1
            zoomTarget.width = this.dog.dog.width * 1.4
            zoomTarget.height = this.dog.dog.height
            undoZoom = CameraZoom.zoom(this.game, zoomTarget, 1000, Scale.width() * 0.02)
            this.scientist.slideTo(Scale.width() - (this.scientist.width * 0.5))
        })

        oneOffs.add(9500, () => {
            Sprites.fade(this.pavlov)
        })

        oneOffs.add(10000, () => {
            this.dog.headUp()
        })
        oneOffs.add(12000, () => {
            this.dog.rest()
        })

        oneOffs.add(14000, () => {
            Sprites.fade(this.pavlov, {
                fadeIn: false, fadeDuration: 300, onComplete: () => {
                    this.pavlov.destroy()
                }
            })
        })

        let bell: Phaser.Sprite

        const addBell = () => {
            bell = this.game.add.sprite(MenPos.bellX(), MenPos.bellY(), MentalAssets.BELL)
            Scale.scaleSprite(bell, 0.9)
            bell.animations.add(MentalState.RING, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 4, 3, 2, 1, 0])
        }

        oneOffs.add(15000, () => {
            addBell()
        })

        oneOffs.add(16000, () => {
            bell.animations.play(MentalState.RING, 15, false, false)
            this.slideMeatBowlIn()
        })
        oneOffs.add(16500, () => {
            this.bellSound.play()
        })

        oneOffs.add(17000, () => {
            this.dog.sitUp()
            this.dog.tailWagFast()
        })

        oneOffs.add(20000, () => {
            this.dog.drool()
        })

        oneOffs.add(24000, () => {
            this.fadeMeatBowlOut()
            bell.destroy()
        })
        oneOffs.add(24500, () => {
            this.dog.tailWag()
        })

        oneOffs.add(25000, () => {
            addBell()
            bell.animations.play(MentalState.RING, 15, false, false)
            this.slideMeatBowlIn()
        })
        oneOffs.add(25500, () => {
            this.bellSound.play()
        })

        oneOffs.add(26000, () => {
            this.dog.tailWagFast()
            this.dog.drool()
        })

        oneOffs.add(27500, () => {
            bell.destroy()
        })

        const thoughtBellMeat = this.game.add.sprite(MenPos.thoughtX(), MenPos.thoughtY(),
            MentalAssets.THOUGHT_BELL_MEAT)
        Scale.scaleSprite(thoughtBellMeat, 1)
        thoughtBellMeat.visible = false
        oneOffs.add(29000, () => {
            thoughtBellMeat.visible = true
            thoughtBellMeat.animations.add(MentalState.THOUGHT)
            thoughtBellMeat.animations.play(MentalState.THOUGHT, 6, false, false)
        })

        oneOffs.add(30000, () => {
            this.fadeMeatBowlOut()
        })

        oneOffs.add(31000, () => {
            this.dog.tailWag()
        })

        oneOffs.add(32000, () => {
            thoughtBellMeat.destroy()
        })

        oneOffs.add(33000, () => {
            addBell()
            bell.animations.play(MentalState.RING, 15, false, false)
        })
        oneOffs.add(33500, () => {
            this.bellSound.play()
        })

        oneOffs.add(33500, () => {
            this.dog.tailWagFast()
            this.dog.drool(true)
        })

        const thoughtMeat = this.game.add.sprite(MenPos.thoughtX(), MenPos.thoughtY(), MentalAssets.THOUGHT_MEAT)
        Scale.scaleSprite(thoughtMeat, 1)
        thoughtMeat.visible = false
        oneOffs.add(35500, () => {
            bell.destroy()
            this.meatBowl.destroy()
            thoughtMeat.visible = true
            thoughtMeat.animations.add(MentalState.THOUGHT)
            thoughtMeat.animations.play(MentalState.THOUGHT, 4, false, false)
        })

        oneOffs.add(37000, () => {
            CameraZoom.performZoom(undoZoom)
            this.scientist.slideTo(MenPos.scientistX())
            thoughtMeat.destroy()
        })

        // ***** BELOW HERE 2ND SCENE TIMERS ***** //

        oneOffs.add(40000, () => {
            this.title.destroy()
        })

        oneOffs.add(41750, () => {
            Sprites.fadeOut(this.rug)
        })

        oneOffs.add(41000, () => {
            const zoomTarget = new ZoomTarget()
            zoomTarget.x = MenPos.scientistX()
            zoomTarget.y = 0
            zoomTarget.width = Scale.width()
            zoomTarget.height = Scale.height()
            CameraZoom.zoom(this.game, zoomTarget, 1000)
        })

        oneOffs.add(45000, () => {
            Sprites.fade(this.coffeeAlone)
        })

        oneOffs.add(51000, () => {
            undoZoom = CameraZoom.zoom(this.game, this.projector, 1000, Scale.width() * 0.02)
        })

        oneOffs.add(55000, () => {
            CameraZoom.zoomSprite(this.coffeeAlone, this.coffeeAlone.x, MenPos.coffeeSceneY2(),
                MentalState.COFFEE_SCENE_SIZE_SMALL)
        })

        oneOffs.add(56000, () => {
            Sprites.fade(this.coffeeTogether, {
                onComplete: () => {
                    this.coffeeAlone.visible = false
                }
            })
        })

        const coffeeCrossTimer = this.game.time.create()
        const crossMoveAmount = Scale.width() * 0.01
        coffeeCrossTimer.loop(15, () => {
            this.ecig.x -= crossMoveAmount
            this.coffeeCup.x += crossMoveAmount
            if (this.ecig.x <= MenPos.ecigX2() && this.coffeeCup.x >= MenPos.coffeeX2()) {
                coffeeCrossTimer.destroy()
            }
        })

        oneOffs.add(57000, () => {
            Sprites.fadeOut(this.coffeeTogether)
            coffeeCrossTimer.start()
        })

        oneOffs.add(60000, () => {
            Sprites.fadeOut(this.coffeeCup)
            Sprites.fadeOut(this.ecig)
            Sprites.fade(this.coffeeOnly)
            Sprites.fade(this.coffeeSmell)
        })

        oneOffs.add(61500, () => {
            log.debug("Playing thought ecig")
            const thoughtEcig = this.game.add.sprite(MenPos.thoughtEcigX(), MenPos.thoughtEcigY(),
                MentalAssets.THOUGHT_ECIG)
            Scale.scaleSprite(thoughtEcig, 1.2)
            thoughtEcig.animations.add(MentalState.THOUGHT)
            thoughtEcig.animations.play(MentalState.THOUGHT, 2, false, false)
        })

        oneOffs.add(66000, () => {
            CameraZoom.performZoom(undoZoom)
        })

        oneOffs.start()
    }

    private slideMeatBowlIn = () => {
        this.meatBowl.alpha = 1
        CameraZoom.moveSprite(this.meatBowl, MenPos.foodDishX2())
    }

    private fadeMeatBowlOut = () => {
        const meatBowlFadeOut = this.game.time.create()
        meatBowlFadeOut.loop(10, () => {
            this.pavlov.alpha -= 0.03
            if (this.pavlov.alpha <= 0) {
                this.meatBowl.x = MenPos.foodDishX()
                meatBowlFadeOut.destroy()
            }
        })
        meatBowlFadeOut.start()
    }

}
